import React, { createContext, useContext, ReactNode } from 'react';
import { useExperimentsStore } from 'apps/experiments/src/store';
import { canEditEntity, normalizeExperimentEntity } from 'common/report/util/saved-entities';
import { Entity, EntityType } from 'common/report/util/saved-entities/types';
import { SegmentFilterController } from 'common/charts/contexts/segment-filter-controller';
interface ExperimentPageContextProps {
  projectId: number;
  workspaceId: number;
  userId: number;
  experimentId?: string;
  experimentEntity?: Entity | null;
  featureFlagIntegrationEnabled: boolean;
  canEdit?: boolean;
  segmentFilterController?: SegmentFilterController;
}
const ExperimentPageContext = createContext<ExperimentPageContextProps | undefined>(undefined);
export const ExperimentPageProvider: React.FC<ExperimentPageContextProps & {
  children: ReactNode;
}> = _ref => {
  let {
    projectId,
    workspaceId,
    experimentId,
    userId,
    featureFlagIntegrationEnabled,
    children
  } = _ref;
  const segmentFilterController = React.useRef(new SegmentFilterController());
  const currentExperiment = useExperimentsStore(store => store.experiment);
  const experimentEntity = React.useMemo(() => normalizeExperimentEntity(currentExperiment), [currentExperiment]);
  const canEdit = React.useMemo(() => experimentEntity ? canEditEntity({
    entity: experimentEntity,
    entityType: EntityType.Experiment,
    projectId
  }) : false, [experimentEntity, projectId]);
  return <ExperimentPageContext.Provider value={{
    projectId,
    workspaceId,
    experimentId,
    userId,
    featureFlagIntegrationEnabled,
    experimentEntity,
    canEdit,
    segmentFilterController: segmentFilterController.current
  }} data-sentry-element="unknown" data-sentry-component="ExperimentPageProvider" data-sentry-source-file="context.tsx">
      {children}
    </ExperimentPageContext.Provider>;
};
export const useExperimentPageContext = (): ExperimentPageContextProps => {
  const context = useContext(ExperimentPageContext);
  if (!context) {
    console.error("useExperimentPageContext must be used within an ExperimentPageProvider");
    return {
      projectId: 0,
      workspaceId: 0,
      userId: 0,
      experimentId: undefined,
      experimentEntity: null,
      featureFlagIntegrationEnabled: false,
      canEdit: false
    };
  }
  return context;
};